import { createContext, useContext, type ReactNode } from "react"
import { useMatches } from "@tanstack/react-router"

/**
 * Marks the subtree as "inside a Module inspector". Provided by {@link ModuleInspector}
 * so collapsible cards can opt into persisting their expand state only when rendered in a
 * inspector (and not when the same card is used in main content / dialogs elsewhere).
 */
const InspectorExpansionContext = createContext(false)

export const InspectorExpansionProvider = ({ children }: { children: ReactNode }) => (
  <InspectorExpansionContext.Provider value={true}>{children}</InspectorExpansionContext.Provider>
)

/**
 * Stable persist key for a collapsible inspector card, scoped to the current route so
 * expand state is independent per tab automatically. Returns `undefined` when the card is
 * not inside a Module inspector — pass that straight to `ExpandableCard`'s `persistKey`,
 * which then falls back to ephemeral state.
 *
 * `sectionId` must be stable and unique within a single inspector (e.g. "details",
 * "comments", "adjustments").
 */
export const useInspectorExpandKey = (sectionId: string): string | undefined => {
  const inInspector = useContext(InspectorExpansionContext)
  const matches = useMatches()
  const routeId = matches.at(-1)?.routeId ?? "unknown"
  // Old "sidepanel:" prefix kept deliberately: these keys are persisted, so renaming it
  // would drop every user's saved expand/collapse state.
  return inInspector ? `sidepanel:${routeId}:${sectionId}` : undefined
}
